/**
 * MainLayout – Premium sidebar + animated content area.
 * Desktop: collapsible sidebar with streak badge.
 * Mobile: 5 tab bottom nav.
 */
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home, Calendar, PlusSquare, Bell, Search, BarChart2, Settings,
  Video, LogOut, ChevronLeft, ChevronRight, Trophy, Sparkles, BookHeart, Map,
  Flame,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@store/authStore";
import { OnboardingGate } from "@components/onboarding/OnboardingGate";
import { LevelBadge } from "@components/gamification/LevelBadge";
import { challengesApi } from "@services/api/challenges";

const NAV_ITEMS = [
  { to: "/lifemap",      icon: Map,         labelKey: "nav.lifemap",       exact: false, emoji: "📸" },
  { to: "/feed",            icon: Home,        labelKey: "nav.feed",          exact: true,  emoji: "🏠" },
  { to: "/new",          icon: PlusSquare,  labelKey: "nav.new_post",      exact: false, emoji: "✍️" },
  { to: "/calendar",     icon: Calendar,    labelKey: "nav.calendar",      exact: false, emoji: "📅" },
  { to: "/ai",           icon: Sparkles,    labelKey: "nav.ai",            exact: false, emoji: "🤖" },
  { to: "/profile",      icon: Settings,    labelKey: "nav.profile",       exact: false, emoji: "👤" },
  { to: "/video",        icon: Video,       labelKey: "nav.video",         exact: false, emoji: "🎥" },
  { to: "/challenges",   icon: Trophy,      labelKey: "nav.challenges",    exact: false, emoji: "🏆" },
  { to: "/highlights",   icon: Sparkles,    labelKey: "nav.highlights",    exact: false, emoji: "✨" },
  { to: "/journal",      icon: BookHeart,   labelKey: "nav.journal",       exact: false, emoji: "📓" },
  { to: "/analytics",    icon: BarChart2,   labelKey: "nav.analytics",     exact: false, emoji: "📊" },
  { to: "/search",       icon: Search,      labelKey: "nav.search",        exact: false, emoji: "🔍" },
  { to: "/notifications",icon: Bell,        labelKey: "nav.notifications", exact: false, emoji: "🔔" },
  { to: "/settings",     icon: Settings,    labelKey: "nav.settings",      exact: false, emoji: "⚙️" },
];

const NAV_GROUPS = [
  { label: "Asosiy",  items: NAV_ITEMS.slice(0, 6) },
  { label: "Kontent", items: NAV_ITEMS.slice(6, 10) },
  { label: "Boshqa",  items: NAV_ITEMS.slice(10) },
];

const MOBILE_BOTTOM_NAV = [
  { to: "/feed", icon: Home, labelKey: "nav.feed", exact: true, isCreate: false },
  { to: "/lifemap", icon: Map, labelKey: "nav.lifemap", exact: false, isCreate: false },
  { to: "/new", icon: PlusSquare, labelKey: "nav.new_post", exact: false, isCreate: true },
  { to: "/ai", icon: Sparkles, labelKey: "nav.ai", exact: false, isCreate: false },
  { to: "/profile", icon: Settings, labelKey: "nav.profile", exact: false, isCreate: false },
];

function NavItem({
  to, icon: Icon, labelKey, exact, collapsed,
}: (typeof NAV_ITEMS)[0] & { collapsed: boolean }) {
  const { t } = useTranslation();
  return (
    <NavLink
      to={to}
      end={exact}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 group relative
         ${isActive
           ? "bg-amber-500/12 text-amber-200 border border-amber-500/20"
           : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/60"
         }`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <motion.div
              layoutId="nav-indicator"
              className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-amber-400 rounded-full"
              transition={{ type: "spring", stiffness: 400, damping: 35 }}
            />
          )}
          <Icon size={18} className={`shrink-0 ${isActive ? "text-amber-400" : "text-slate-500 group-hover:text-slate-300"}`} />
          <AnimatePresence>
            {!collapsed && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: "auto" }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden whitespace-nowrap"
              >
                {t(labelKey)}
              </motion.span>
            )}
          </AnimatePresence>
        </>
      )}
    </NavLink>
  );
}

export default function MainLayout() {
  const { user, logout } = useAuthStore();
  const location = useLocation();
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);

  const isWidePage =
    location.pathname.startsWith("/lifemap") ||
    location.pathname.startsWith("/reels") ||
    location.pathname.startsWith("/@") ||
    location.pathname.startsWith("/event/");

  // Fetch streak from challenges API (stable endpoint)
  const { data: streakData } = useQuery({
    queryKey: ["streak-sidebar"],
    queryFn: () =>
      challengesApi.getLevel().then((r) => r.data.data).catch(() => null),
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  const currentStreak = (streakData?.daily_streak ?? 0) as number;
  const isOnFire = currentStreak >= 7;

  const handleLogout = async () => {
    await logout();
  };

  const sidebarWidth = collapsed ? "w-[68px]" : "w-60";

  return (
    <div className="min-h-screen text-slate-100 flex overflow-x-hidden" style={{ backgroundColor: "var(--bg-base)" }}>
      <OnboardingGate />
      {/* ── Decorative background orbs ───────────────────────────────────────── */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="bg-orb bg-orb-primary" />
        <div className="bg-orb bg-orb-secondary" />
      </div>

      {/* ── Sidebar (desktop ≥ md) ────────────────────────────────────────────── */}
      <motion.aside
        animate={{ width: collapsed ? 68 : 240 }}
        transition={{ type: "spring", stiffness: 300, damping: 35 }}
        className={`hidden md:flex flex-col glass border-r border-[var(--border-subtle)] fixed h-full z-20 overflow-hidden`}
      >
        {/* Logo + collapse toggle */}
        <div className="flex items-center justify-between px-3 py-5 border-b border-[var(--border-subtle)]">
          <NavLink to="/" className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 shrink-0 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-lg">
              L
            </div>
            <AnimatePresence>
              {!collapsed && (
                <motion.span
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="text-base font-bold gradient-text truncate"
                >
                  Lifegram
                </motion.span>
              )}
            </AnimatePresence>
          </NavLink>

          <button
            onClick={() => setCollapsed(!collapsed)}
            className="shrink-0 w-6 h-6 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-slate-800/60 transition-colors flex items-center justify-center"
          >
            {collapsed
              ? <ChevronRight size={14} />
              : <ChevronLeft size={14} />
            }
          </button>
        </div>

        {currentStreak > 0 && (
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mx-3 mt-3 px-3 py-2 rounded-xl border border-orange-500/20 bg-orange-500/8 flex items-center gap-2 ${collapsed ? "justify-center" : ""}`}
            >
              <motion.div
                animate={isOnFire ? { scale: [1, 1.2, 1], rotate: [0, -5, 5, 0] } : {}}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Flame size={16} className="text-orange-400 shrink-0" />
              </motion.div>
              <AnimatePresence>
                {!collapsed && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="min-w-0"
                  >
                    <span className="text-xs font-bold streak-flame">{currentStreak} kun</span>
                    <span className="text-[10px] text-slate-500 ml-1">ketma-ket</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </AnimatePresence>
        )}

        {!collapsed && (
          <div className="mx-3 mb-2">
            <LevelBadge />
          </div>
        )}

        {/* Nav groups */}
        <div className="flex-1 overflow-y-auto no-scrollbar py-3 px-2 space-y-4">
          {NAV_GROUPS.map((group) => (
            <div key={group.label}>
              {!collapsed && (
                <p className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider px-3 mb-1">
                  {group.label}
                </p>
              )}
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavItem key={item.to} {...item} collapsed={collapsed} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* User section */}
        <div className="border-t border-[var(--border-subtle)] p-2 space-y-1">
          <NavLink
            to="/profile"
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-800/60 transition-all group"
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold shrink-0 shadow-md">
              {user?.username?.[0]?.toUpperCase() ?? "U"}
            </div>
            <AnimatePresence>
              {!collapsed && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="min-w-0 flex-1"
                >
                  <div className="text-sm font-medium text-slate-200 truncate">
                    {user?.displayName || user?.username}
                  </div>
                  <div className="text-[10px] text-indigo-400 capitalize font-medium">{user?.plan} plan</div>
                </motion.div>
              )}
            </AnimatePresence>
          </NavLink>

          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-slate-500 hover:text-red-400 hover:bg-red-500/8 transition-all text-sm ${collapsed ? "justify-center" : ""}`}
          >
            <LogOut size={16} />
            {!collapsed && <span>{t("common.logout")}</span>}
          </button>
        </div>
      </motion.aside>

      {/* ── Main content ──────────────────────────────────────────────────────── */}
      <motion.main
        animate={{
          marginLeft:
            typeof window !== "undefined" && window.innerWidth >= 768
              ? collapsed
                ? 68
                : 240
              : 0,
        }}
        transition={{ type: "spring", stiffness: 300, damping: 35 }}
        className="flex-1 min-h-screen relative z-10"
      >
        <div className={`mx-auto w-full min-w-0 px-3 sm:px-4 py-4 sm:py-6 pb-[calc(5.5rem+env(safe-area-inset-bottom))] md:pb-8 ${isWidePage ? "max-w-full lg:max-w-5xl" : "max-w-3xl"}`}>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 10, scale: 0.99 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.99 }}
              transition={{ duration: 0.22, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.main>

      {/* ── Mobile bottom navigation (5 tab) ─────────────────────────────────── */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-30 glass border-t border-[var(--border-subtle)] safe-area-pb">
        <div className="flex justify-around items-center h-16 px-1">
          {MOBILE_BOTTOM_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-xl transition-all min-w-[56px]
                 ${item.isCreate ? "" : isActive ? "text-amber-400" : "text-slate-500"}`
              }
            >
              {({ isActive }) =>
                item.isCreate ? (
                  <div className="w-12 h-12 -mt-5 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
                    <item.icon size={22} className="text-white" />
                  </div>
                ) : (
                  <>
                    <div className={`relative ${isActive ? "scale-110" : ""} transition-transform`}>
                      <item.icon size={20} />
                    </div>
                    <span className="text-[9px] font-medium">{t(item.labelKey)}</span>
                  </>
                )
              }
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
